import { z } from 'zod';
import { AssistantError, type SourceFact } from './assistant';
import { appointmentTimeWindowSchema } from './appointment-time-window';

// Declared here because ./assistant reaches this module while it is loading.
const timestampSchema = z.iso.datetime({ offset: true });
const slotSchema = z.object({
  startsAt: timestampSchema,
  endsAt: timestampSchema,
});

// The result of one calendar availability read, as the appointment engine
// reports it. It travels as the content of a `calendar:` source fact; every
// engine and model adapter reads it through calendarAvailabilityEvidence so
// that all of them share this one contract. Field order is the written order.
export const calendarAvailabilityEvidenceSchema = z.object({
  serviceId: z.string().nullable().optional(),
  timezone: z.string().min(1),
  appointmentKind: z
    .enum(['appointment', 'site_visit', 'sales_call'])
    .optional(),
  availableSlots: z.array(slotSchema),
  hasMore: z.boolean().optional(),
  nextOffset: z.number().int().nullable().optional(),
  requestedTimeAvailable: z.boolean().nullable().optional(),
  nearestAlternatives: z.array(slotSchema).optional(),
  searchPreference: z
    .object({
      serviceId: z.string().nullable().optional(),
      localDate: z.iso.date().nullable(),
      weekday: z.number().int().min(0).max(6).nullable().optional(),
      timeOfDay: z.enum(['any', 'morning', 'afternoon']).optional(),
      localTime: z.string().nullable(),
      timeWindow: appointmentTimeWindowSchema.nullable().optional(),
    })
    .optional(),
  observedAt: timestampSchema.optional(),
});
export type CalendarAvailabilityEvidence = z.infer<
  typeof calendarAvailabilityEvidenceSchema
>;

const invalid = () =>
  new AssistantError('INVALID_CALENDAR_AVAILABILITY_EVIDENCE', 502);

export function calendarAvailabilityContent(
  evidence: CalendarAvailabilityEvidence
): string {
  const parsed = calendarAvailabilityEvidenceSchema.safeParse(evidence);
  if (!parsed.success) throw invalid();
  return JSON.stringify(parsed.data);
}

// Availability carried by a fact, or null when the fact is something else: a
// source that is not a calendar, or a calendar booking receipt. A calendar fact
// that lists slots but breaks the contract is an error, never an empty calendar.
export function calendarAvailabilityEvidence(
  fact: Pick<SourceFact, 'source' | 'content'>
): CalendarAvailabilityEvidence | null {
  if (!fact.source.startsWith('calendar:')) return null;
  let content: unknown;
  try {
    content = JSON.parse(fact.content);
  } catch {
    throw invalid();
  }
  if (
    !content ||
    typeof content !== 'object' ||
    Array.isArray(content) ||
    !('availableSlots' in content)
  )
    return null;
  const parsed = calendarAvailabilityEvidenceSchema.safeParse(content);
  if (!parsed.success) throw invalid();
  return parsed.data;
}

const calendarBookingEvidenceSchema = z.object({
  providerBookingId: z.string().min(1),
  status: z.enum(['confirmed', 'cancelled']),
  startsAt: timestampSchema,
  endsAt: timestampSchema,
});

// The booking a calendar provider acknowledged after a booking, a move or a
// cancellation, as the receipt fact of that action carries it. Null when the
// fact is something else; a receipt that breaks the contract is an error.
export function calendarBookingEvidence(
  fact: Pick<SourceFact, 'source' | 'content'>
): {
  status: 'confirmed' | 'cancelled';
  startsAt: string;
  endsAt: string;
} | null {
  if (!fact.source.startsWith('calendar:')) return null;
  let content: unknown;
  try {
    content = JSON.parse(fact.content);
  } catch {
    return null;
  }
  if (
    !content ||
    typeof content !== 'object' ||
    Array.isArray(content) ||
    !('providerBookingId' in content)
  )
    return null;
  const parsed = calendarBookingEvidenceSchema.safeParse(content);
  if (!parsed.success)
    throw new AssistantError('INVALID_CALENDAR_BOOKING_EVIDENCE', 502);
  const { status, startsAt, endsAt } = parsed.data;
  return { status, startsAt, endsAt };
}
